import axios from 'axios';
import * as Sentry from '@sentry/react';
import { AuthResponse, LoginDto, RegisterDto, CreateBookingDto } from '../types';
import { API_BASE_URL } from '../config/api';

/**
 * Type for generic API query parameters.
 * Use specific interfaces where available.
 */
type ApiParams = Record<string, string | number | boolean | undefined>;

/**
 * Type for API request body data.
 * Use specific DTOs where available.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiRequestBody = Record<string, any>;

/**
 * Barbershop search/filter parameters
 */
interface BarbershopSearchParams extends ApiParams {
  name?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Booking query parameters
 */
interface BookingQueryParams extends ApiParams {
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Checkout session request
 */
interface CheckoutSessionRequest {
  tier: string;
  successUrl: string;
  cancelUrl: string;
}

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Single-flight token refresh state (FE-1).
 *
 * The backend ROTATES the refresh token on every /auth/refresh-token call and
 * rejects any request presenting a stale token. When an access token expires on
 * a data-heavy page, several requests 401 in parallel; if each one POSTed the
 * refresh endpoint independently, only the first would win and the rest would
 * present the now-rotated token → all losers get rejected, localStorage is wiped
 * and the user is hard-redirected to /login (a sporadic full logout).
 *
 * Fix: exactly ONE refresh runs at a time. The first 401 starts the refresh and
 * stores the in-flight promise; every concurrent 401 (and any request that 401s
 * WHILE the refresh is in flight) awaits that same promise, then retries with the
 * single new access token. The slot resets to null once it settles so the next
 * expiry can refresh again.
 */
let refreshPromise: Promise<string> | null = null;

/**
 * Perform the actual token refresh. Reads the current tokens, POSTs the refresh
 * endpoint, and persists the rotated pair ONCE. Throws when no tokens are present
 * or the endpoint rejects — callers treat a throw as "log the user out".
 */
async function performTokenRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken');
  const accessToken = localStorage.getItem('accessToken');

  if (!refreshToken || !accessToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh-token`, {
    accessToken,
    refreshToken,
  });

  const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

  localStorage.setItem('accessToken', newAccessToken);
  localStorage.setItem('refreshToken', newRefreshToken);

  return newAccessToken;
}

/**
 * Clear local auth state and hard-redirect to the login page. Kept identical to
 * the previous inline behavior so the AuthContext contract (same localStorage
 * keys, same redirect target) is preserved.
 */
function forceLogout(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
}

/**
 * Start (or reuse) the single in-flight refresh. The logout-on-failure is owned
 * HERE — attached exactly once to the promise that is actually created — so a
 * failed refresh logs the user out exactly ONCE no matter how many requests are
 * queued behind it. Awaiters just reject their own request; they never re-trigger
 * logout.
 */
function getRefreshPromise(): Promise<string> {
  if (!refreshPromise) {
    const inFlight = performTokenRefresh();
    // Awaiters consume `inFlight` directly (success → new token, failure → reject
    // their own request). This side chain owns the logout and the slot reset:
    // `.catch` swallows the rejection (logout fires once), so `.finally` never
    // re-throws — no unhandled rejection, slot resets whether refresh won or lost.
    inFlight
      .catch(() => {
        // Single owner of the logout: fires once per failed refresh cycle.
        forceLogout();
      })
      .finally(() => {
        refreshPromise = null;
      });
    refreshPromise = inFlight;
  }
  return refreshPromise;
}

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 501 Not Implemented: the backend returns { error: "..." } with a friendly
    // message for endpoints that exist but aren't built yet (see docs/BACKLOG.md, CS-4).
    // Promote that message onto error.message so callers that display err.message or
    // use getErrorMessage() show it instead of "Request failed with status code 501".
    if (error.response?.status === 501) {
      const serverMessage = error.response?.data?.error;
      if (typeof serverMessage === 'string' && serverMessage.trim()) {
        error.message = serverMessage;
      } else {
        error.message = 'This feature is not available yet. Please check back soon.';
      }
      console.warn(`Not implemented (501): ${originalRequest?.url ?? 'unknown endpoint'}`);
      return Promise.reject(error);
    }

    // Report unexpected failures to Sentry (no-op when Sentry isn't initialized):
    // - server errors (5xx) — 501 never reaches here (handled above as an
    //   expected "not built yet" flow)
    // - network failures (no response at all), excluding caller-cancelled requests
    // 4xx responses are intentionally NOT captured — they are expected flows
    // (validation errors, 401 refresh below, 404s, etc.).
    const status: number | undefined = error.response?.status;
    const isServerError = typeof status === 'number' && status >= 500;
    const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';
    if (isServerError || isNetworkError) {
      const method = originalRequest?.method?.toUpperCase() ?? 'UNKNOWN';
      const endpoint = originalRequest?.url ?? 'unknown';
      Sentry.captureException(error, {
        tags: {
          'api.endpoint': endpoint,
        },
        contexts: {
          api: {
            method,
            endpoint,
            status: status ?? 'network-error',
          },
        },
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // A 401 from the refresh endpoint itself must NOT recurse into another
      // refresh — the refresh token is dead. Go straight to logout.
      const url: string = originalRequest?.url ?? '';
      if (url.includes('/auth/refresh-token')) {
        forceLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Guard: nothing to refresh with → straight to logout (don't POST a null token).
      const hasTokens =
        localStorage.getItem('refreshToken') && localStorage.getItem('accessToken');
      if (!hasTokens) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        // Single-flight: the first 401 starts the refresh; concurrent 401s (and
        // requests that 401 mid-refresh) await the SAME promise instead of each
        // POSTing the rotating refresh token.
        const newAccessToken = await getRefreshPromise();

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed. The logout/redirect is owned by getRefreshPromise and
        // has already fired exactly once — here we only reject this request.
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (credentials: LoginDto): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: RegisterDto): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  refreshToken: async (tokens: { accessToken: string; refreshToken: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/refresh-token', tokens);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/revoke-token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  forgotPassword: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },

  resetPassword: async (data: { email: string; token: string; newPassword: string; confirmPassword: string }): Promise<void> => {
    await api.post('/auth/reset-password', data);
  },

  /**
   * Confirm an email address from the emailed link. Pass the DECODED userId and
   * token (as returned by URLSearchParams.get) — axios re-encodes params, and
   * Identity tokens contain `+`, `/` and `=`, so pre-encoding would double-encode.
   */
  confirmEmail: async (userId: string, token: string): Promise<{ message: string }> => {
    const response = await api.get('/auth/confirm-email', { params: { userId, token } });
    return response.data;
  },

  // Invite-only launch methods
  getInviteOnlyStatus: async (): Promise<{ inviteOnlyEnabled: boolean }> => {
    const response = await api.get('/auth/invite-only-status');
    return response.data;
  },

  validateInviteCode: async (code: string): Promise<{ isValid: boolean; message?: string }> => {
    const response = await api.post('/auth/validate-invite-code', { code });
    return response.data;
  },
};

// Barbershops API
export const barbershopsApi = {
  getAll: async (params?: BarbershopSearchParams) => {
    const response = await api.get('/barbershops', { params });
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/barbershops/${id}`);
    return response.data;
  },

  getBarbers: async (id: number) => {
    const response = await api.get(`/barbershops/${id}/barbers`);
    return response.data;
  },

  search: async (params?: BarbershopSearchParams) => {
    const response = await api.get('/barbershops/search', { params });
    return response.data;
  },

  // Shop Owner endpoints
  getMyBarbershops: async () => {
    const response = await api.get('/barbershops/my-barbershops');
    return response.data;
  },

  getMyBarbershop: async (id: number) => {
    const response = await api.get(`/barbershops/my-barbershops/${id}`);
    return response.data;
  },

  create: async (data: ApiRequestBody) => {
    const response = await api.post('/barbershops', data);
    return response.data;
  },

  update: async (id: number, data: ApiRequestBody) => {
    const response = await api.put(`/barbershops/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/barbershops/${id}`);
    return response.data;
  },

  // Barber management
  createBarber: async (barbershopId: number, data: ApiRequestBody) => {
    const response = await api.post(`/barbershops/${barbershopId}/barbers`, {
      ...data,
      barbershopId
    });
    return response.data;
  },

  updateBarber: async (barberId: number, data: ApiRequestBody) => {
    const response = await api.put(`/barbershops/barbers/${barberId}`, data);
    return response.data;
  },

  deleteBarber: async (barberId: number) => {
    const response = await api.delete(`/barbershops/barbers/${barberId}`);
    return response.data;
  },
};

// Bookings API (core booking CRUD + owner/barber day summaries).
// NOTE: distinct from the richer `bookingsApi` in ./bookingApi. This one is
// exported as `bookingsCoreApi` to avoid a same-name export collision — a wrong
// import path would otherwise silently swap the API surface.
export const bookingsCoreApi = {
  getMyBookings: async (params?: BookingQueryParams) => {
    const response = await api.get('/bookings/my-bookings', { params });
    return response.data;
  },

  create: async (data: CreateBookingDto) => {
    const response = await api.post('/bookings', data);
    return response.data;
  },

  update: async (id: number, data: ApiRequestBody) => {
    const response = await api.put(`/bookings/${id}`, data);
    return response.data;
  },

  updateBookingStatus: async (id: number, status: string) => {
    const response = await api.patch(`/bookings/${id}/status`, { Status: status });
    return response.data;
  },

  cancel: async (id: number, reason?: string) => {
    const response = await api.post(`/bookings/${id}/cancel`, { cancellationReason: reason });
    return response.data;
  },

  cancelBooking: async (id: number, reason?: string) => {
    const response = await api.post(`/bookings/${id}/cancel`, { cancellationReason: reason });
    return response.data;
  },

  updateNotes: async (id: number, notes: string) => {
    const response = await api.patch(`/bookings/${id}/notes`, { notes });
    return response.data;
  },

  getTodaySummaryForOwner: async (date?: string) => {
    const response = await api.get('/bookings/today-summary/owner', {
      params: date ? { date } : undefined
    });
    return response.data;
  },

  getTodaySummaryForBarber: async (barberId: number, date?: string) => {
    const response = await api.get(`/bookings/today-summary/barber/${barberId}`, {
      params: date ? { date } : undefined
    });
    return response.data;
  },

  getBarberBookings: async (barberId: number, page: number = 1, pageSize: number = 100) => {
    const response = await api.get(`/bookings/barber/${barberId}`, {
      params: { page, pageSize, _t: Date.now() }  // Add timestamp to prevent caching
    });
    return response.data;
  },
};

// Subscriptions API
export const subscriptionsApi = {
  getCurrent: async () => {
    const response = await api.get('/subscriptions/current');
    return response.data;
  },

  create: async (tier: string) => {
    const response = await api.post('/subscriptions', { tier });
    return response.data;
  },

  cancel: async (id: number) => {
    const response = await api.post(`/subscriptions/${id}/cancel`);
    return response.data;
  },

  getUsage: async () => {
    const response = await api.get('/subscriptions/usage');
    return response.data;
  },

  createCheckoutSession: async (data: CheckoutSessionRequest) => {
    const response = await api.post('/subscriptions/checkout', data);
    return response.data;
  },
};

// Customer Billing Portal API (Stripe Customer Portal)
export const customerPortalApi = {
  /**
   * Create a Stripe Customer Portal session.
   * @param returnUrl URL to return to after the portal — may be a relative path
   *                  (e.g., '/subscription'); the backend resolves it against the
   *                  configured frontend URL.
   * @returns The portal session URL to redirect the user to.
   */
  createSession: async (returnUrl: string): Promise<{ url: string }> => {
    const response = await api.post('/customer-portal/session', { returnUrl });
    return response.data;
  },
};

export default api;